<template>
  <div class="billing-config-form">
    <h3 class="section-title">Configurações de Faturamento</h3>

    <!-- Tipo de Cobrança -->
    <div class="form-group">
      <label for="billing_type">Tipo de Cobrança</label>
      <select
        id="billing_type"
        v-model="formData.billing_type"
        class="form-control"
        @change="onBillingTypeChange"
      >
        <option
          v-for="option in BILLING_TYPE_OPTIONS"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <small class="form-text text-muted">
        Defina como o tenant será cobrado mensalmente
      </small>
    </div>

    <!-- Valor Fixo -->
    <div v-if="formData.billing_type === BILLING_TYPES.FIXED_VALUE" class="form-group">
      <label for="billing_fixed_value">Valor Fixo Mensal *</label>
      <div class="input-group">
        <div class="input-group-prepend">
          <span class="input-group-text">R$</span>
        </div>
        <input
          id="billing_fixed_value"
          v-model.number="formData.billing_fixed_value"
          type="number"
          step="0.01"
          min="0"
          class="form-control"
          placeholder="0,00"
          required
        />
      </div>
      <small class="form-text text-muted">
        Valor mensal fixo independente do número de usuários
      </small>
    </div>

    <!-- Por Usuário Ativo -->
    <div v-if="formData.billing_type === BILLING_TYPES.PER_ACTIVE_USER" class="form-group">
      <label for="billing_per_user_value">Valor por Usuário Ativo *</label>
      <div class="input-group">
        <div class="input-group-prepend">
          <span class="input-group-text">R$</span>
        </div>
        <input
          id="billing_per_user_value"
          v-model.number="formData.billing_per_user_value"
          type="number"
          step="0.01"
          min="0"
          class="form-control"
          placeholder="0,00"
          required
        />
      </div>
      <small class="form-text text-muted">
        Valor cobrado por cada usuário que fizer checkin no período
      </small>
    </div>

    <!-- Por Faixa de Usuários -->
    <div v-if="formData.billing_type === BILLING_TYPES.USER_RANGE" class="form-group">
      <label>Faixas de Usuários *</label>
      <BillingUserRanges v-model="formData.billing_user_ranges" />
      <small class="form-text text-muted">
        Configure valores diferentes para cada faixa de usuários cadastrados
      </small>
    </div>

    <!-- Período de Faturamento -->
    <div v-if="formData.billing_type" class="row">
      <div class="col-md-6">
        <div class="form-group">
          <label for="billing_period_start_day">Dia de Início do Período</label>
          <input
            id="billing_period_start_day"
            v-model.number="formData.billing_period_start_day"
            type="number"
            min="1"
            max="31"
            class="form-control"
            placeholder="1"
          />
          <small class="form-text text-muted">
            Dia do mês em que o período de faturamento inicia (1-31)
          </small>
        </div>
      </div>

      <div class="col-md-6">
        <div class="form-group">
          <label for="billing_period_end_day">Dia de Fim do Período</label>
          <input
            id="billing_period_end_day"
            v-model.number="formData.billing_period_end_day"
            type="number"
            min="1"
            max="31"
            class="form-control"
            placeholder="20"
          />
          <small class="form-text text-muted">
            Dia do mês em que o período de faturamento termina (1-31)
          </small>
        </div>
      </div>
    </div>

    <!-- Integração Conexa -->
    <div v-if="formData.billing_type" class="mt-4">
      <h4 class="section-subtitle">Integração Conexa (Opcional)</h4>

      <div class="form-group">
        <label for="conexa_external_id">ID Externo Conexa</label>
        <input
          id="conexa_external_id"
          v-model="formData.conexa_external_id"
          type="text"
          class="form-control"
          placeholder="TENANT_123"
        />
        <small class="form-text text-muted">
          Identificador do tenant na plataforma Conexa
        </small>
      </div>

      <div class="form-group">
        <label for="conexa_contract_id">ID do Contrato Conexa</label>
        <input
          id="conexa_contract_id"
          v-model="formData.conexa_contract_id"
          type="text"
          class="form-control"
          placeholder="CONTRACT_456"
        />
        <small class="form-text text-muted">
          ID do contrato existente (se houver)
        </small>
      </div>
    </div>

    <!-- Preview do Fechamento -->
    <BillingPreview
      v-if="formData.billing_type"
      :billing-config="formData"
      class="mt-4"
    />
  </div>
</template>

<script>
import { BILLING_TYPES, BILLING_TYPE_OPTIONS, DEFAULT_PERIOD_START_DAY, DEFAULT_PERIOD_END_DAY } from '@constants/billing'
import BillingUserRanges from './BillingUserRanges.vue'
import BillingPreview from './BillingPreview.vue'

export default {
  name: 'BillingConfigForm',

  components: {
    BillingUserRanges,
    BillingPreview,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      BILLING_TYPES,
      BILLING_TYPE_OPTIONS,
      formData: {
        billing_type: null,
        billing_fixed_value: null,
        billing_per_user_value: null,
        billing_user_ranges: [],
        billing_period_start_day: DEFAULT_PERIOD_START_DAY,
        billing_period_end_day: DEFAULT_PERIOD_END_DAY,
        billing_email: null,
        conexa_external_id: null,
        conexa_contract_id: null,
      },
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue !== this.formData) {
          // Ensure billing_user_ranges is always an array
          if (typeof newValue.billing_user_ranges === 'string') {
            try {
              newValue = { ...newValue, billing_user_ranges: JSON.parse(newValue.billing_user_ranges) }
            } catch (e) {
              newValue = { ...newValue, billing_user_ranges: [] }
            }
          } else if (!Array.isArray(newValue.billing_user_ranges)) {
            newValue = { ...newValue, billing_user_ranges: [] }
          }
          // Merge only billing-related fields
          const billingFields = [
            'billing_type', 'billing_fixed_value', 'billing_per_user_value',
            'billing_user_ranges', 'billing_period_start_day', 'billing_period_end_day',
            'billing_email', 'conexa_external_id', 'conexa_contract_id'
          ]
          billingFields.forEach(field => {
            if (Object.prototype.hasOwnProperty.call(newValue, field)) {
              this.formData[field] = newValue[field]
            }
          })
        }
      },
    },

    formData: {
      deep: true,
      handler(newValue) {
        // Emit only billing-related fields
        const billingData = {
          billing_type: newValue.billing_type,
          billing_fixed_value: newValue.billing_fixed_value,
          billing_per_user_value: newValue.billing_per_user_value,
          billing_user_ranges: newValue.billing_user_ranges,
          billing_period_start_day: newValue.billing_period_start_day,
          billing_period_end_day: newValue.billing_period_end_day,
          billing_email: newValue.billing_email,
          conexa_external_id: newValue.conexa_external_id,
          conexa_contract_id: newValue.conexa_contract_id,
        }
        this.$emit('input', { ...this.value, ...billingData })
      },
    },
  },

  methods: {
    onBillingTypeChange() {
      // Limpa valores quando muda o tipo
      this.formData.billing_fixed_value = null
      this.formData.billing_per_user_value = null
      this.formData.billing_user_ranges = []
    },
  },
}
</script>

<style scoped>
.billing-config-form {
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 8px;
}

.section-title {
  margin-bottom: 20px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.section-subtitle {
  margin-bottom: 15px;
  font-size: 1rem;
  font-weight: 600;
  color: #555;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-text {
  margin-top: 5px;
  font-size: 0.875rem;
}
</style>
